/**
 * Skeleton loading screen for the standalone (full-page) conversation layout.
 *
 * Uses `StandaloneLayout` to replicate the two-column desktop grid and
 * single-column mobile layout without duplicating layout logic. The nav bar,
 * header placeholder, left-rail comment list, and right-rail cards are each
 * filled with skeleton primitives from `component/skeleton`.
 */

import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { Pad, Separator } from 'component/basics';
import { spacings } from 'component/constants';
import { BylineSkeleton } from 'component/progress/byline-skeleton';
import { CommentSkeleton } from 'component/progress/comment-skeleton';
import { LoadingAnnouncement } from 'component/progress/loading-announcement';
import { NavBarSkeleton } from 'component/progress/nav-bar-skeleton';
import { PanelSkeleton } from 'component/progress/panel-skeleton';
import { PromptSkeleton } from 'component/progress/prompt-skeleton';
import { StoryCardSkeleton } from 'component/progress/story-card-skeleton';
import { TextContainerSkeleton } from 'component/progress/text-container-skeleton';
import { View } from 'react-native';
import { useIsDesktopBreakpoint } from 'util/layout';

import { StandaloneLayout } from './standalone-layout';

function SkeletonEditorialCard() {
    const s = useStyles();
    return (
        <PanelSkeleton>
            <TextContainerSkeleton borderRadius={2} height={6} width={140} />
            <Pad size={spacings.xl2} />
            <View style={s.bylineItem}>
                <BylineSkeleton />
            </View>
            <Pad size={spacings.lg} />
            <Separator />
            <Pad size={spacings.lg} />
            <View style={s.bylineItem}>
                <BylineSkeleton />
            </View>
        </PanelSkeleton>
    );
}

function SkeletonRelatedContentCard() {
    const s = useStyles();
    return (
        <View style={s.relatedContentCard}>
            <TextContainerSkeleton borderRadius={2} height={6} width={140} />
            <Pad size={spacings.xl2} />
            <StoryCardSkeleton />
            <Pad size={spacings.lg} />
            <Separator />
            <Pad size={spacings.lg} />
            <StoryCardSkeleton />
            <Pad size={spacings.lg} />
            <Separator />
            <Pad size={spacings.lg} />
            <StoryCardSkeleton />
        </View>
    );
}

export function SkeletonStandaloneConversationScreen() {
    const isDesktop = useIsDesktopBreakpoint();
    const s = useStyles();
    const { colors } = useTheme();

    return (
        <View style={s.outer}>
            <LoadingAnnouncement />
            <NavBarSkeleton large={isDesktop} />
            <StandaloneLayout
                header={<View style={[s.headerPlaceholder, { height: isDesktop ? 110 : 188 }]} />}
                headerBackgroundColor={colors.colorSurfaceSecondary}
                rightRail={
                    <>
                        <SkeletonEditorialCard />
                        <Pad size={spacings.xl2} />
                        <SkeletonRelatedContentCard />
                    </>
                }
            >
                <PromptSkeleton />
                <View style={s.sortRow}>
                    <TextContainerSkeleton borderRadius={2} height={6} width={60} />
                </View>
                {Array.from({ length: isDesktop ? 4 : 3 }).map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <CommentSkeleton key={i} variant={isDesktop ? 'desktop' : 'mobile'} />
                ))}
            </StandaloneLayout>
        </View>
    );
}

const useStyles = createThemedStyles((tokens) => ({
    outer: { flex: 1, backgroundColor: tokens.colors.colorSurfacePrimary },
    headerPlaceholder: { width: '100%', backgroundColor: tokens.colors.colorSurfaceSecondary },
    sortRow: { paddingHorizontal: 20, paddingVertical: 16 },
    bylineItem: { paddingHorizontal: spacings.lg },
    relatedContentCard: {
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        borderRadius: spacings.sm,
        padding: spacings.xl2,
    },
}));
